// PrivacyPolicy.jsx
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    if (window.history.length <= 1) {
      navigate('/', { replace: true }); // cambia '/' con la tua pagina di fallback se preferisci
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleGoBack();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-800 to-indigo-900 py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Freccia indietro fissa in alto a sinistra */}
      <button
        onClick={handleGoBack}
        aria-label="Torna alla pagina precedente"
        title="Torna indietro"
        className="fixed top-4 left-4 z-50 inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-slate-900/70 px-3 py-2 text-gray-100 backdrop-blur-sm hover:bg-slate-800/80 focus:outline-none focus:ring-2 focus:ring-blue-400"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="hidden sm:inline">Back</span>
      </button>

      <div className="max-w-3xl mx-auto bg-gray-800/50 rounded-xl shadow-lg overflow-hidden p-8 backdrop-blur-sm border border-gray-700">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-blue-300 mb-2">Privacy Policy</h1>
          <p className="text-gray-400">Last updated: {new Date().toLocaleDateString('en-GB')}</p>
        </div>

        <div className="prose prose-invert max-w-none text-gray-300 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">1. Information We Collect</h2>
            <p>
              We collect various types of personal data from you when you use our services or interact with our website. This includes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Contact information such as name, email address, postal address, and phone number.</li>
              <li>Account information when you create an account, including login credentials.</li>
              <li>Payment and billing information when you make purchases.</li>
              <li>Technical data including IP address, browser type, operating system, and browsing behavior.</li>
              <li>Usage data from interactions with our platform and communications.</li>
            </ul>
            <p>
              We collect this data directly from you or through automated technologies such as cookies and web beacons.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">2. How We Use Your Information</h2>
            <p>We use your personal data for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide, maintain, and improve our services and website.</li>
              <li>To process transactions, fulfill orders, and manage accounts.</li>
              <li>To communicate with you, respond to inquiries, and send important notices.</li>
              <li>To personalize your experience and provide tailored content and offers.</li>
              <li>To comply with legal obligations and protect our rights and property.</li>
              <li>To monitor usage and enhance security, including fraud prevention.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">3. Legal Basis for Processing</h2>
            <p>
              Where applicable, we process your personal data based on your consent, the necessity to perform a contract, compliance with legal obligations, or our legitimate interests to run and improve our business.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">4. Data Sharing and Disclosure</h2>
            <p>
              We do not sell your personal data. We may share your information with:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Trusted third-party service providers who perform services on our behalf (e.g., payment processors, email services, analytics).</li>
              <li>Law enforcement or regulatory agencies when required by law or to protect our rights.</li>
              <li>Affiliated companies within our corporate group.</li>
            </ul>
            <p>All third parties are required to keep your data confidential and secure.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">5. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies, web beacons, and similar technologies to collect information about your browsing behavior and device. This helps us enhance your user experience, analyze site traffic, and serve relevant advertising.
            </p>
            <p>
              You can manage your cookie preferences via your browser settings. Disabling cookies may affect site functionality.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">6. Data Security</h2>
            <p>
              We employ reasonable technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">7. Your Rights</h2>
            <p>
              Depending on your jurisdiction, you may have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The right to access your data and receive a copy.</li>
              <li>The right to correct inaccurate or incomplete data.</li>
              <li>The right to request deletion of your data ("right to be forgotten").</li>
              <li>The right to restrict or object to processing.</li>
              <li>The right to data portability.</li>
              <li>The right to withdraw consent at any time where processing is based on consent.</li>
              <li>The right to lodge a complaint with a data protection authority.</li>
            </ul>
            <p>
              To exercise these rights, please contact us at: <a href="mailto:support@sweet-trading.com" className="text-blue-400 underline">support@sweet-trading.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">8. Data Retention</h2>
            <p>
              We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, or as required by law. When no longer needed, we securely delete or anonymize your data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">9. International Transfers</h2>
            <p>
              Your data may be transferred to and processed in countries outside your jurisdiction, including countries that may not have the same data protection laws. We ensure appropriate safeguards are in place such as Standard Contractual Clauses or Privacy Shield certifications where applicable.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">10. Children’s Privacy</h2>
            <p>
              Our services are not intended for individuals under the age of 16 (or applicable minimum age in your country). We do not knowingly collect personal data from children. If you believe we have inadvertently collected such data, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We encourage you to review this page periodically. Continued use of our services after updates constitutes acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-blue-200 mb-4">12. Contact Us</h2>
            <p>
              For questions or concerns about this Privacy Policy or our data practices, please contact:
            </p>
            <address className="not-italic">
              Sweet Trading<br />
              Email: <a href="mailto:support@sweet-trading.com" className="text-blue-400 underline">support@sweet-trading.com</a><br />
            </address>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-gray-700 text-center">
          <Link to="/terms" className="text-blue-400 hover:text-blue-300 inline-flex items-center">
            Also see our Terms and Conditions
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
